// The new unified HackerLabs entry point. One screen, one bar, one
// orchestration layer. Replaces the old `/` (Index) experience.
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import FunctionStrip from "@/components/ai-home/FunctionStrip";
import MemoryPopover from "@/components/ai-home/MemoryPopover";
import LibrarySheet from "@/components/ai-home/LibrarySheet";
import HackathonPotCard from "@/components/ai-home/HackathonPotCard";
import UnifiedMenu from "@/components/ai-home/UnifiedMenu";
import WalletSheet from "@/components/wallet/WalletSheet";
import SubscriptionNudgeDialog from "@/components/wallet/SubscriptionNudgeDialog";
import MultiChat from "@/components/ai-home/MultiChat";
import { cn } from "@/lib/utils";

export default function HomeAI() {
  // Library still seeds prompts via window event.
  const seedPrompt = (p: string) => {
    window.dispatchEvent(new CustomEvent("multichat-seed", { detail: p }));
  };

  // The footer is pulled up by default. As soon as the user starts
  // typing in the chat search or an LLM begins responding, we slide
  // it down so the conversation owns the screen.
  const [chatActive, setChatActive] = useState(false);
  useEffect(() => {
    const onActivity = (e: Event) => {
      const detail = (e as CustomEvent<{ active: boolean }>).detail;
      setChatActive(Boolean(detail?.active));
    };
    window.addEventListener("multichat-activity", onActivity as EventListener);
    return () => window.removeEventListener("multichat-activity", onActivity as EventListener);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-x-hidden">
      <Helmet>
        <title>hackerlabs.ai — one bar, every AI, get it done</title>
        <meta name="description" content="The unified AI operating layer. One bar routes your prompt to the cheapest capable model, lets you swap to a stronger one, and shares what you make with the community." />
        <link rel="canonical" href="https://hackerlabs.ai/" />
      </Helmet>

      {/* Ambient background glow */}
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-primary/10 blur-[140px]" />
      </div>

      {/* Single right-side menu with everything inside. */}
      <UnifiedMenu />

      {/* Wallet sheet is mounted (no trigger) so the right-side pulldown
          can pop it open via the `open-wallet` window event. */}
      <HomeWalletMount />
      <MemoryPopover hideTrigger />
      <LibrarySheet hideTrigger onRun={(p) => seedPrompt(p)} />

      <main className="container mx-auto px-4 pt-24 sm:pt-28 pb-24 max-w-3xl">
        <HackathonPotCard />
        <div className="text-center mb-8">
          <Link to="/" className="inline-block" aria-label="hackerlabs.ai home">
            <span className="text-3xl sm:text-5xl font-mono font-extrabold tracking-tight lowercase">
              <span className="text-foreground">hacker</span>
              <span className="text-primary">labs</span>
              <span className="text-muted-foreground">.ai</span>
            </span>
          </Link>
        </div>

        <MultiChat />
        <FunctionStrip />
      </main>

      {/* Footer: pulled up when idle, pushed off-screen during chat. */}
      <div
        aria-hidden={chatActive}
        className={cn(
          "fixed inset-x-0 bottom-0 z-20 transition-transform duration-300 ease-out",
          chatActive ? "translate-y-full" : "translate-y-0",
        )}
      >
      </div>
    </div>
  );
}

// Tiny helper so the wallet sheet listens for the `open-wallet` event
// the unified menu fires. Keeps HomeAI tidy.
function HomeWalletMount() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("open-wallet", onOpen);
    return () => window.removeEventListener("open-wallet", onOpen);
  }, []);
  return (
    <>
      <WalletSheet open={open} onOpenChange={setOpen} />
      <SubscriptionNudgeDialog />
    </>
  );
}
